import { useState, useMemo, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Badge } from "@/shared/components/ui/badge";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Flyout } from "@/shared/components/ui/flyout";
import { RadioGroup, RadioGroupSegment } from "@/shared/components/ui/radio-group";
import { Plus, Search, Server } from "lucide-react";
import { Integration, IntegrationKind } from "../types/integration";
import { IntegrationService } from "../services/integrationsService";
import { getIntegrationDescription } from '@/shared/utils/i18nHelper';
import { sameOriginProxyUrl } from "@/shared/utils/proxyUrl";
import { filterCatalog, isPendingReview } from "../utils/integrationKind";

interface IntegrationsSelectorProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectTemplate: (template: Integration) => void;
  /** "Adicionar novo": servidor MCP que ainda não está no catálogo (AB#87477). */
  onAddMcpServer: () => void;
}

/** O filtro abre sempre em MCP Servers (AB#87477). */
const DEFAULT_KIND: IntegrationKind = "mcp";

const IntegrationsSelector = ({
  isOpen,
  onClose,
  onSelectTemplate,
  onAddMcpServer,
}: IntegrationsSelectorProps) => {
  const { t, i18n } = useTranslation();
  const [templateSearchQuery, setTemplateSearchQuery] = useState("");
  const [kind, setKind] = useState<IntegrationKind>(DEFAULT_KIND);

  // SEGURANÇA: Limpar campo de pesquisa sempre que o modal abrir — e voltar o filtro
  // para MCP Servers, o padrão de cada abertura.
  // Várias instâncias do mesmo template são permitidas (AB#87477): o seletor não consulta
  // mais o que já está instalado para desabilitar card.
  useEffect(() => {
    if (isOpen) {
      setTemplateSearchQuery("");
      setKind(DEFAULT_KIND);
    }
  }, [isOpen]);

  // Buscar templates disponíveis
  const {
    data: templates,
    isLoading: isLoadingTemplates
  } = useQuery({
    queryKey: ["integration-templates"],
    queryFn: () => IntegrationService.getAll(),
  });

  // Filtrar por tipo e pesquisa; bloqueados (settings.block) não aparecem para seleção.
  const filteredTemplates = useMemo(
    () =>
      filterCatalog(templates ?? [], {
        kind,
        query: templateSearchQuery,
        describe: (template) => getIntegrationDescription(template, i18n.language),
      }),
    [templates, kind, templateSearchQuery, i18n.language],
  );

  const handleSelectTemplate = (template: Integration) => {
    onSelectTemplate(template);
    handleClose();
  };

  const handleAddNew = () => {
    onAddMcpServer();
    handleClose();
  };

  const addMcpServerButton = (
    <Button className="w-full gap-1" onClick={handleAddNew}>
      <Plus className="h-4 w-4" />
      {t("integrations.selector.addMcpServer")}
    </Button>
  );

  const handleClose = () => {
    // SEGURANÇA: Sempre limpar campo de pesquisa ao fechar
    setTemplateSearchQuery("");
    onClose();
  };

  const renderTemplate = (template: Integration) => {
    const isInactive = template.active === false;
    const logoUrl = sameOriginProxyUrl(template.logo_url);

    return (
      <Card
        key={template.id}
        className={`transition-shadow ${
          isInactive
            ? 'opacity-40 grayscale cursor-not-allowed'
            : 'hover:shadow-md cursor-pointer hover:border-primary/50'
        }`}
        onClick={() => !isInactive && handleSelectTemplate(template)}
      >
        <CardContent className="p-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              {logoUrl ? (
                <img
                  src={logoUrl}
                  alt={template.label}
                  className="aspect-square h-full w-full object-contain p-1.5"
                />
              ) : (
                <Server className="h-5 w-5 text-primary" />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h3 className="font-semibold text-sm">{template.label}</h3>
                {/* Servidor que esta organização trouxe: já é dela, e entra no catálogo de todos
                    depois da revisão do time do Prodgy. */}
                {isPendingReview(template) && (
                  <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                    {t("integrations.review.pending")}
                  </Badge>
                )}
              </div>
              <p className="text-xs text-muted-foreground line-clamp-2">
                {getIntegrationDescription(template, i18n.language) || t('integrations.empty.description')}
              </p>
              {kind === "mcp" && typeof template.tool_count === "number" && (
                <p className="text-[11px] text-muted-foreground/80">
                  {t("integrations.tools.count", { count: template.tool_count })}
                </p>
              )}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                disabled={isInactive}
              >
                <Plus className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <Flyout
      open={isOpen}
      onClose={handleClose}
      footer={
        // Só em MCP Servers: é onde um servidor que não está no catálogo entra pela URL.
        kind === "mcp" ? (
          <div className="space-y-2">
            <p className="text-xs text-muted-foreground">{t("integrations.selector.notFound")}</p>
            {addMcpServerButton}
          </div>
        ) : undefined
      }
    >
      <div className="space-y-6">
        {/* Header do flyout */}
        <div className="pt-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 rounded-full bg-primary/10">
              <Plus className="h-5 w-5 text-primary" />
            </div>
            <h2 className="text-xl font-semibold">{t("integrations.addIntegration")}</h2>
          </div>
          <p className="text-sm text-muted-foreground">
            {t("integrations.selectTemplate")}
          </p>
        </div>

        {/* Tipo (AB#87477): um só selecionado, MCP Servers a cada abertura. */}
        <RadioGroup
          value={kind}
          onValueChange={(value) => setKind(value === "api" ? "api" : "mcp")}
          aria-label={t("integrations.selector.kindFilter")}
          className="grid w-full grid-cols-2 gap-1 rounded-lg bg-muted p-1"
        >
          <RadioGroupSegment value="mcp">{t("integrations.selector.kinds.mcp")}</RadioGroupSegment>
          <RadioGroupSegment value="api">{t("integrations.selector.kinds.api")}</RadioGroupSegment>
        </RadioGroup>

        {/* Search Bar no flyout */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground h-4 w-4" />
          <Input
            placeholder={t("integrations.searchTools")}
            aria-label={t("integrations.searchTools")}
            value={templateSearchQuery}
            onChange={(e) => setTemplateSearchQuery(e.target.value)}
            className="pl-10 h-10 bg-background"
          />
        </div>

        {/* Lista de templates */}
        <div className="space-y-3">
          {isLoadingTemplates ? (
            <div className="flex items-center justify-center py-8">
              <div className="text-sm text-muted-foreground">{t("common.loading")}</div>
            </div>
          ) : filteredTemplates.length > 0 ? (
            filteredTemplates.map((template) => renderTemplate(template))
          ) : kind === "mcp" ? (
            <div className="space-y-3 rounded-lg border border-dashed py-8 text-center">
              <Server className="mx-auto h-8 w-8 text-muted-foreground" />
              <p className="text-sm font-medium">{t("integrations.selector.noMcpServers")}</p>
              <p className="text-xs text-muted-foreground">{t("integrations.selector.noMcpServersHint")}</p>
              <div className="mx-auto max-w-xs">{addMcpServerButton}</div>
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              {t('integrations.selector.noToolsFound')}
            </div>
          )}
        </div>

      </div>
    </Flyout>
  );
};

export default IntegrationsSelector;
